import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import {
  DEFAULT_DIFFICULTY,
  difficultyProfile,
  type DifficultyProfile,
} from '../config/difficulty';
import { Rng } from '../core/Rng';
import type { WeaponTarget } from '../combat/Weapon';
import { ProjectileSystem } from '../combat/Projectiles';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { Room } from '../world/Room';
import { Drone } from './Drone';

export interface DronePlacement {
  /** A surface key: `zone:face` for multi-zone levels, `face` otherwise. */
  surface: string;
  position: Vector3;
  /** Magnetic drones can be pulled by the grapple hook. */
  magnetic?: boolean;
  /** An authored patrol loop, in world metres. */
  route?: Array<[number, number, number]>;
}

const scratchDirection = new Vector3();
const scratchJittered = new Vector3();
const scratchLineOfSight = new Vector3();
const scratchSpawn = new Vector3();
const scratchLead = new Vector3();

/**
 * Owns the drones in the level: spawning, hovering, and the bolts they fire
 * at the player.
 */
export class DroneController {
  private readonly drones: Drone[] = [];
  private readonly projectiles = new ProjectileSystem();
  private time = 0;
  /** The level's difficulty tier, applied to every drone it spawns. */
  private profile: DifficultyProfile = difficultyProfile(DEFAULT_DIFFICULTY);

  constructor(
    private readonly room: Room,
    /** Used for the line-of-sight check; without it every drone shoots freely. */
    private readonly collision?: CollisionWorld,
  ) {}

  get list(): readonly Drone[] {
    return this.drones;
  }

  /**
   * Set the difficulty tier for the drones this controller spawns. Called once
   * per level, before the first spawn.
   */
  setProfile(profile: DifficultyProfile): void {
    this.profile = profile;
  }

  /** The difficulty tier in force, for tests and tooling. */
  get difficultyProfile(): DifficultyProfile {
    return this.profile;
  }

  get bolts(): ProjectileSystem {
    return this.projectiles;
  }

  get remaining(): number {
    let alive = 0;
    for (const drone of this.drones) {
      if (!drone.isDestroyed) alive += 1;
    }
    return alive;
  }

  get allDestroyed(): boolean {
    return this.drones.length > 0 && this.remaining === 0;
  }

  /** True while any living drone is awake because it saw or heard the player. */
  get anyAlerted(): boolean {
    return this.drones.some((drone) => !drone.isDestroyed && drone.isAlerted);
  }

  /**
   * A noise at `position` reaches every drone inside `radius`. Returns the
   * number of drones that heard it, so the game can give feedback once.
   */
  hearNoise(position: Vector3, radius: number): number {
    let heard = 0;
    for (const drone of this.drones) {
      if (drone.isDestroyed) continue;
      if (drone.position.distanceTo(position) > radius) continue;
      if (drone.hearNoise()) heard += 1;
    }
    return heard;
  }

  spawn(placements: DronePlacement[], seed: number): void {
    this.drones.length = 0;
    this.projectiles.clear();
    this.time = 0;

    const rng = new Rng(seed);
    placements.forEach((placement, index) => {
      const surface = this.room.getSurface(placement.surface) ?? null;
      // Each drone gets its own deterministic stream for firing, so the
      // staggered cadence and aim jitter replay exactly for a given seed
      // without disturbing the appearance rolls.
      const fireRng = new Rng((seed ^ Math.imul(index + 1, 0x9e3779b1)) >>> 0);
      this.drones.push(
        new Drone(
          `drone-${index}`,
          placement.position,
          surface,
          rng,
          fireRng,
          placement.magnetic ?? false,
          this.profile,
          placement.route,
        ),
      );
    });
  }

  /** Targets that the player's weapon can hit. */
  get weaponTargets(): WeaponTarget[] {
    return this.drones
      .filter((drone) => !drone.isDestroyed)
      .map((drone) => ({
        id: drone.id,
        position: drone.position,
        radius: drone.radius,
        isDestroyed: drone.isDestroyed,
        takeDamage: (amount: number) => drone.takeDamage(amount),
      }));
  }

  update(dt: number, playerPosition: Vector3, playerVelocity?: Vector3): void {
    this.time += dt;

    for (const drone of this.drones) {
      const canSee = this.canEngage(drone, playerPosition);
      const wantsToFire = drone.update(this.time, dt, canSee);
      if (!wantsToFire || drone.isDestroyed) continue;
      this.fireAtPlayer(drone, playerPosition, playerVelocity);
    }
  }

  /** True when the drone is in range and has a clear line to the player. */
  private canEngage(drone: Drone, playerPosition: Vector3): boolean {
    const distance = playerPosition.distanceTo(drone.position);
    if (distance > BALANCE.combat.droneEngageRange || distance < 0.5) return false;
    return this.hasLineOfSight(drone.position, playerPosition);
  }

  /** True when nothing solid stands between the drone and the player. */
  private hasLineOfSight(from: Vector3, to: Vector3): boolean {
    if (!this.collision) return true;
    scratchLineOfSight.subVectors(to, from);
    const length = scratchLineOfSight.length();
    if (length < 1e-4) return true;
    scratchLineOfSight.divideScalar(length);

    const hit = this.collision.raycast(from, scratchLineOfSight, length);
    // A hit that lands short of the target means something is in the way.
    return !hit || hit.distance >= length - 0.25;
  }

  private fireAtPlayer(drone: Drone, playerPosition: Vector3, playerVelocity?: Vector3): void {
    // The state machine already checked range and line of sight this frame, so
    // the shot only needs to aim.
    scratchDirection.subVectors(playerPosition, drone.position);
    const distance = scratchDirection.length();
    if (distance < 1e-4) return;

    // Lead a moving target so strafing does not make the drone harmless.
    if (playerVelocity && playerVelocity.lengthSq() > 1e-4) {
      const leadTime = (distance / BALANCE.combat.boltSpeed) * this.profile.leadFactor;
      scratchLead.copy(playerPosition).addScaledVector(playerVelocity, leadTime);
      scratchDirection.subVectors(scratchLead, drone.position);
    }
    scratchDirection.normalize();
    drone.applyAimJitter(scratchDirection, scratchJittered);

    // Start the bolt just outside the shell so it is visible as it leaves the
    // drone instead of appearing inside the model.
    scratchSpawn.copy(drone.position).addScaledVector(scratchJittered, drone.radius + 0.05);

    this.projectiles.spawn(
      scratchSpawn,
      scratchJittered,
      BALANCE.combat.boltSpeed,
      BALANCE.combat.boltDamage,
      BALANCE.combat.boltLifetime,
    );
  }
}
